<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <component :is="isInSduiList ? 'li' : 'div'"
             :class="$attrs.class"
             :style="$attrs.style"
  >
    <component
      v-bind="filteredAttrs"
      :is="(link && 'SLink') || (Object.entries($listeners).length !== 0 && 'button') || 'div'"
      :aria-describedby="isInSduiList && !$store.state.isIOS && Object.entries($listeners).length !== 0 ? `sdui-list-heading-${listId}` : undefined"
      :class="[
        'sdui-list-item justify-content-between d-flex align-items-center position-relative',
        {
          'sdui-list-item--hoverable': isHoverable,
          'sdui-list-item--disabled': disabled,
        },
      ]"
      :type="Object.entries($listeners).length !== 0 ? 'button' : undefined"
      :href="link || undefined"
      :tabindex="disabled ? '-1' : null"
      :disabled="disabled"
      :is-sdui-list-item="link ? true : null"
      :data-test="computedDataTest"
      v-on="$listeners"
    >
      <!-- icon -->
      <span
        v-if="icon || iconText || image || $hasSlotContent($slots['icon']) || $hasSlotContent($slots['image'])"
        class="sdui-list-item__icon-wrapper mr-2 position-relative"
      >
        <slot name="image">
          <img
            v-if="image"
            :src="image"
            width="36"
            height="36"
            alt=""
            loading="lazy"
            data-test="sdui-list-item-image"
            class="sdui-list-item__image"
          />
        </slot>
        <slot name="icon">
          <sdui-icon
            v-if="icon || iconText"
            data-test="sdui-list-item-icon"
            class="sdui-list-item__icon"
            :class="[
              'sdui-icon',
              iconColorClasses,
              {
                'sdui-list-item__icon--big': iconSize === 'lg',
              },
            ]"
          >
            <span v-if="iconText"
                  data-test="sdui-list-item-icon-text"
                  class="sdui-list-item__icon-text"
            >{{ iconText }}</span>
          </sdui-icon>

          <span
            v-if="$hasSlotContent($slots['icon-additional-top'])"
            class="sdui-list-item__icon-additional sdui-list-item__icon-additional--top position-absolute"
            data-test="sdui-list-item-icon-additional-slot-top"
          >
            <slot name="icon-additional-top"></slot>
          </span>

          <span
            v-if="$hasSlotContent($slots['icon-additional-bottom'])"
            class="sdui-list-item__icon-additional sdui-list-item__icon-additional--bottom position-absolute"
            data-test="sdui-list-item-icon-additional-slot-bottom"
          >
            <slot name="icon-additional-bottom"></slot>
          </span>
        </slot>
      </span>

      <!-- content -->
      <span
        v-if="title || $hasSlotContent($slots['item-title']) || description || $hasSlotContent($slots['description'])"
        class="sdui-list-item__content mw-0"
      >
        <span class="sdui-list-item__title-wrapper">
          <slot name="item-title" :aria-describedby="isInSduiList && !$store.state.isIOS ? `sdui-list-heading-${listId}` : undefined">
            <span
              v-if="title"
              data-test="sdui-list-item-title-wrapper"
              class="sdui-list-item__title w-100"
              :class="{ primary: primaryTextColor, 'font-weight-bolder': primaryTextColor, 'text-truncate d-block': truncateTitle }"
              data-cy="sdui-item-title"
            >
              <span data-test="sdui-list-item-title">{{ title }}</span>
              <span v-if="$hasSlotContent($slots['icon-title'])"
                    data-test="sdui-list-item-icon-title"
                    class="ml-1"
              >
                <slot name="icon-title" />
              </span>
              <b-badge v-if="badgeTitle"
                       data-test="sdui-list-item-badge"
                       variant="secondary"
                       class="small ml-1"
              >
                {{ badgeTitle }}
              </b-badge>
              <portal-target :name="portalId" tag="span" />
            </span>
          </slot>
        </span>

        <slot name="description">
          <p
            v-if="description"
            data-test="sdui-list-item-description"
            class="sdui-list-item__description nano text-muted m-0"
            :class="{ 'text-truncate d-block': truncateDescription }"
          >
            {{ description }}
          </p>
        </slot>

        <ul v-if="badges.length" class="sdui-list-item__badges list-unstyled mb-0 d-flex flex-wrap mt-1">
          <li v-for="badge in badges" :key="badge.text">
            <b-badge class="d-flex align-items-center"
                     :class="badgeClasses(badge)"
                     :data-cy="badge.testId"
            >
              <sdui-icon v-if="badge.icon"
                         class="small mr-1"
                         :class="badge.icon"
              ></sdui-icon>
              {{ badge.text }}
            </b-badge>
          </li>
        </ul>
      </span>

      <span class="sdui-list-item__right-action-wrapper d-flex flex-column" :class="{ 'justify-content-end': !datetime }">
        <time
          v-if="datetime"
          data-test="sdui-list-item-date-time"
          class="sdui-list-item__time text-right small position-static"
          :class="[{ 'text-primary': datetimePrimary, 'text-muted': !datetimePrimary }]"
          :datetime="datetime"
        >
          {{ formattedDatetime }}
        </time>

        <!-- right action -->
        <span
          v-if="rightActionIcon || rightActionLabel || rightActionLabelSr || $hasSlotContent($slots['right-action'])"
          class="sdui-list-item__right-action d-flex ml-2"
          :class="{ 'align-items-center': alignRightActionCenter }"
        >
          <slot name="right-action">
            <span v-if="rightActionLabel">
              <p
                data-test="sdui-list-item-right-action-label"
                class="sdui-list-item__label m-0"
                :class="{ 'text-primary': rightActionLabelHighlighted }"
              >
                {{ rightActionLabel }}
              </p>
            </span>

            <sdui-icon
              v-if="rightActionIcon && !rightActionLabelHighlighted"
              data-test="sdui-list-item-right-action-icon"
              class="icon-16 text-muted align-items-center"
              :class="rightActionIcon"
            />
            <span
              v-if="rightActionLabelSr"
              data-test="sdui-list-item-right-action-label-sr"
              class="sr-only"
            > {{ rightActionLabelSr }} </span>
          </slot>
        </span>

        <!-- unread indicator -->
        <span
          v-if="unreadIndicator === 'dot'"
          data-cy="unread-indicator-dot"
          class="unread-indicator--dot d-block bg-primary text-white nano ml-auto rounded-circle"
        >
          <span class="sr-only">{{ $t('chats.message.unread_messages_indicator') }}</span>
        </span>
        <span v-else-if="unreadIndicator" class="unread-indicator bg-primary text-white nano px-1">
          <span class="sr-only">{{ $t('chats.message.unread_messages_indicator') }}</span>
          <span data-cy="unread-indicator">{{ unreadIndicator }}</span>
        </span>
      </span>
    </component>

    <slot name="modals" />

    <slot v-if="isInSduiList" name="collapsable" />
  </component>
</template>

<script>
import { allIcons } from 'src/config/services/allIcons.js'
import { PortalTarget } from 'portal-vue'
import SLink from '@/components/base/SLink.vue'

export default {
  name: 'SListItem',
  components: { PortalTarget, SLink },
  // Prevent inheriting attributes from parent component to root element of this component.
  // This is to prevent having duplicate data-cy/test or aria attributes on the root element.
  inheritAttrs: false,

  // needed for the sdui-list-item component to be wrapped around in a li-tag, when using inside this sdui-list
  inject: {
    isUsedInSduiList: {
      from: 'isUsedInSduiList',
      default: false,
    },

    listId: {
      from: 'listId',
      default: null,
    },
  },

  props: {
    link: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    hoverable: {
      type: Boolean,
      default: false,
    },

    icon: {
      type: String,
      default: '',
      validator: function (value) {
        if (!value) return true
        // We intentionally do allow 'spinner-input', 'expired-icon' and 'pending-user' here even though it's no icon
        // from our font. Those classes are used to indicate certain states of the item.
        const iconArr = value.split(' ')
        const validIcons = [...allIcons(), 'spinner-input', 'expired-icon', 'pending-user']
        return iconArr.every((icon) => validIcons.includes(icon.replace('icon-', '')))
      },
    },

    iconSize: {
      type: String,
      default: 'md',
      validator: (value) => ['md', 'lg'].includes(value),
    },

    iconColor: {
      type: String,
      default: 'primary',
    },

    iconCustomColors: {
      type: String,
      default: '',
    },

    iconText: {
      type: String,
      default: '',
    },

    image: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    truncateTitle: {
      type: Boolean,
      default: false,
    },

    description: {
      type: String,
      default: '',
    },

    truncateDescription: {
      type: Boolean,
      default: false,
    },

    badges: {
      type: Array,
      default: () => [],
      validator(value) {
        return value.every((item) => {
          return (
            typeof item === 'object' &&
            'text' in item &&
            'color' in item &&
            (!('outline' in item) || typeof item.outline === 'boolean') &&
            (!('icon' in item) || typeof item.icon === 'string')
          )
        })
      },
    },

    badgeTitle: {
      type: String,
      default: '',
    },

    primaryTextColor: {
      type: Boolean,
      default: false,
    },

    rightActionIcon: {
      type: String,
      default: '',
      validator: function (value) {
        if (!value) return true
        // We intentionally do allow 'spinner-input' here even though it's no icon from our font. It helps to indicate
        // that user has to wait for API/etc to be finished.
        const validIcons = [...allIcons(), 'spinner-input']
        return validIcons.includes(value.replace('icon-', ''))
      },
    },

    rightActionLabel: {
      type: String,
      default: '',
    },

    rightActionLabelHighlighted: {
      type: Boolean,
      default: false,
    },

    rightActionLabelSr: {
      type: String,
      default: '',
    },

    datetime: {
      type: String,
      default: '',
    },

    datetimeFormat: {
      type: String,
      default: 'fullDate',
      validator: (value) => ['fullDate', 'shortDate', 'time', 'dynamicDateTime', 'dateTime', 'dateMonthShort'].includes(value),
    },

    datetimePrimary: {
      type: Boolean,
      default: false,
    },

    unreadIndicator: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    portalId: null,
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    isInSduiList() {
      return this.isUsedInSduiList || false
    },

    hasRightContent() {
      const hasRightActionSlot = this.$hasSlotContent(this.$slots?.['right-action'])
      return this.rightActionIcon || this.rightActionLabel || hasRightActionSlot
    },

    formattedDatetime() {
      if (!this.datetime) return
      switch (this.datetimeFormat) {
      case 'shortDate':
        return this.$dateFormat.dateshort_monthshort(this.datetime)
      case 'dateTime':
        return this.$dateFormat.date_time(this.datetime)
      case 'time':
        return this.$dateFormat.hour_minute(this.datetime)
      case 'dateMonthShort':
        return this.$dateFormat.date_monthshort(this.datetime)
      case 'dynamicDateTime':
        return this.$moment(this.datetime).calendar(null, {
          sameDay: 'HH:mm',
          nextDay: this.$t('channels.channel_type.dates.next_day'),
          nextWeek: 'dddd',
          lastDay: this.$t('channels.channel_type.dates.last_day'),
          lastWeek: 'dddd',
          sameElse: 'DD.MM.YYYY',
        })
      case 'fullDate':
      default:
        return this.$dateFormat.date(this.datetime)
      }
    },

    isHoverable() {
      // eslint-disable-next-line
      return Object.entries(this.$listeners).length !== 0 || this.link || this.hoverable;
    },

    computedDataTest() {
      return this.$attrs['data-test'] ? this.$attrs['data-test'] : 'sdui-list-item'
    },

    iconColorClasses() {
      return this.iconCustomColors ? `${this.iconCustomColors} ${this.icon}` : `${this.iconColor}-light-bg ${this.iconColor} ${this.icon}`
    },

    alignRightActionCenter() {
      return (!this.datetime && !this.unreadIndicator) || (!this.datetime && this.unreadIndicator)
    },
  },

  methods: {
    badgeClasses(badge) {
      const badgeStyle = badge.outline ? `border border-${badge.color} white-bg` : `badge-${badge.color} `
      // if given badge.color is sth like 'danger-dark', do not produce a class 'text-danger-dark-dark'
      const textColor = badge.color.endsWith('dark') ? `text-${badge.color}` : `text-${badge.color}-dark`
      return `small ${textColor} ${badgeStyle}`
    },
  },

  created() {
    this.portalId = this.$uuid()
  },
}
</script>

<style scoped lang="scss">
.sdui-list-item {
  box-shadow: $sdui-shadow;
  border-radius: 8px;
  border: 1px solid $color-gray-100;
  font-weight: $font-weight-light;
  width: 100%;
  text-align: left;
  color: $color-gray-400;
  min-height: 56px;
  background-color: $color-white;
  padding: 10px 12px;
}

.sdui-list-item--disabled {
  opacity: 0.35;
  pointer-events: none;
  cursor: not-allowed;
}

.sdui-list-item--hoverable {
  cursor: pointer;

  // on mobile: prevent hover effect on touch
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: $color-bg-secondary;
    }
  }

  &.selected::before {
    content: '';
    width: 2px;
    background-color: $color-primary;
    height: 100%;
    left: 0;
    top: 0;
    position: absolute;
    z-index: 2;
  }
}

.sdui-list-item__icon {
  border-radius: 50%;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-around;

  // channel icons should fit the circle
  &.icon-channel-custom-and-teachers-56::before,
  &.icon-channel-students-56::before,
  &.icon-channel-parents-56::before {
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 36px;
  }
}

.sdui-list-item__icon--big {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 36px;
  min-width: 56px;
  min-height: 56px;

  // channel icons should fit the circle
  &.icon-channel-custom-and-teachers-56::before,
  &.icon-channel-students-56::before,
  &.icon-channel-parents-56::before {
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 56px;
  }
}

.sdui-list-item__icon-additional--bottom {
  right: 0;
  bottom: 0;
}

.sdui-list-item__icon-additional--top {
  right: 0;
  top: 0;
}

// additional icon needs a little shift on default iconSize variant
.sdui-list-item__icon:not(.sdui-list-item__icon--big) + .sdui-list-item__icon-additional--top {
  transform: translate(25%, -25%);
}

// additional icon needs a little shift on default iconSize variant
.sdui-list-item__icon:not(.sdui-list-item__icon--big) + .sdui-list-item__icon-additional--bottom {
  transform: translate(25%, 25%);
}

.sdui-list-item__icon-additional > * {
  height: 18px;
  width: 18px;
}

.sdui-list-item__icon-text {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.sdui-list-item__icon--big .sdui-list-item__icon-text {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 16px;
}

.sdui-list-item__content {
  width: 100%;
  flex: 1;
}

.sdui-list-item__title-wrapper {
  display: flex;
  align-items: center;
}

// ensure long item-title slot hyphens
.sdui-list-item__title-wrapper > * {
  hyphens: auto;
}

:deep(.sdui-list-item__click-area) {
  color: $color-gray-400;
  font-weight: $font-weight-light;
}

:deep(.sdui-list-item__click-area::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.sdui-list-item__title {
  display: flex;
  align-items: center;
  hyphens: auto;

  :deep(.icon-externallink-o),
  :deep(.icon-download-o) {
    /* stylelint-disable-next-line declaration-property-value-allowed-list */
    font-size: inherit; // to make sure the icon has the same font-size as the title
  }
}

.sdui-list-item__badges {
  gap: 5px;
}

.sdui-list-item__right-action-wrapper {
  gap: 5px;
  align-self: stretch;
  align-items: flex-end;
  max-width: 50%;
  hyphens: auto;
  word-break: break-word;
}

.sdui-list-item__right-action {
  flex: 1;
  gap: 4px;
}

.sdui-list-item__time {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */
  font-size: min(0.75rem, 20px);
  flex: 0;
}

:deep(.sdui-list-item__label){
  text-align: right;
  font-size: pxtorem(13);
  color: $color-gray-300;
  text-overflow: ellipsis;
  overflow: hidden;
}

.sdui-list-item__image {
  object-fit: cover;
}

// set small icon size for circle icons
:deep(.sdui-list-item__right-action .icon.circle) {
  height: 28px;
  width: 28px;
  font-size: pxtorem(16);
}

.unread-indicator {
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 25px;
}

.unread-indicator--dot {
  width: 16px;
  height: 16px;
}
</style>
